import React, { useState } from 'react';
import axios from 'axios';
import PasswordInput from './PasswordInput';

const INITIAL_PASSWORD = '123456789';

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: 'linear-gradient(135deg, #0F1F4D 0%, #0B63C5 100%)',
    fontFamily: 'Inter, system-ui, sans-serif',
    boxSizing: 'border-box'
  },
  card: {
    width: '100%',
    maxWidth: 880,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    background: '#FFFFFF',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(15, 31, 77, 0.45)'
  },
  aside: {
    background: 'linear-gradient(160deg, #0B63C5 0%, #1D4ED8 100%)',
    color: '#FFFFFF',
    padding: '2.5rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  form: { padding: '2.5rem 2rem', display: 'flex', flexDirection: 'column' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 },
  field: { marginBottom: 16 }
};

const Rule = ({ ok, children }) => (
  <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: ok ? '#15803D' : '#64748B' }}>
    <span aria-hidden="true" style={{
      width: 18, height: 18, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, color: '#FFFFFF', background: ok ? '#16A34A' : '#CBD5E1'
    }}>{ok ? '✓' : ''}</span>
    {children}
  </li>
);

const ForcePasswordChange = ({ onChanged, onSignOut, accountLabel }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const rules = {
    length: newPassword.length >= 6,
    notInitial: newPassword.length > 0 && newPassword !== INITIAL_PASSWORD,
    different: newPassword.length > 0 && newPassword !== currentPassword,
    matches: confirmPassword.length > 0 && newPassword === confirmPassword
  };
  const ready = currentPassword && Object.values(rules).every(Boolean);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/auth/change-password', { currentPassword, newPassword, confirmPassword });
      onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <aside style={styles.aside}>
          <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ width: 96, height: 96, borderRadius: 16, background: '#FFFFFF' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.25 }}>Secure your account</h1>
            <p style={{ margin: '0.75rem 0 0', opacity: 0.9, lineHeight: 1.6 }}>
              You signed in with the temporary password. Set a personal password before you continue to your dashboard.
            </p>
          </div>
          {accountLabel && (
            <div style={{ background: 'rgba(255,255,255,0.14)', borderRadius: 12, padding: '0.85rem 1rem' }}>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Signed in as</div>
              <div style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 15, marginTop: 2 }}>{accountLabel}</div>
            </div>
          )}
          <ol style={{ margin: 0, paddingLeft: '1.1rem', lineHeight: 1.9, opacity: 0.95, fontSize: 14 }}>
            <li>Enter the temporary password 123456789</li>
            <li>Choose a new password of at least 6 characters</li>
            <li>Type it again to confirm</li>
          </ol>
        </aside>

        <form onSubmit={submit} style={styles.form}>
          <h2 style={{ margin: '0 0 0.25rem', fontSize: 22, color: '#0F172A' }}>Set a new password</h2>
          <p style={{ margin: '0 0 1.5rem', color: '#64748B', fontSize: 14 }}>All three fields are required.</p>

          <div style={styles.field}>
            <label htmlFor="current-password" style={styles.label}>Current password</label>
            <PasswordInput id="current-password" autoComplete="current-password" placeholder="Temporary password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          </div>
          <div style={styles.field}>
            <label htmlFor="new-password" style={styles.label}>New password</label>
            <PasswordInput id="new-password" autoComplete="new-password" placeholder="At least 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
          </div>
          <div style={styles.field}>
            <label htmlFor="confirm-password" style={styles.label}>Confirm new password</label>
            <PasswordInput id="confirm-password" autoComplete="new-password" placeholder="Type the new password again" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem', display: 'grid', gap: 6 }}>
            <Rule ok={rules.length}>At least 6 characters</Rule>
            <Rule ok={rules.notInitial}>Different from 123456789</Rule>
            <Rule ok={rules.different}>Different from the current password</Rule>
            <Rule ok={rules.matches}>Both new passwords match</Rule>
          </ul>

          {error && (
            <div role="alert" style={{ background: '#FEF2F2', color: '#B91C1C', borderRadius: 8, padding: '0.65rem 0.85rem', fontSize: 14, marginBottom: 12 }}>{error}</div>
          )}

          <button type="submit" disabled={saving || !ready} style={{
            width: '100%', padding: '0.85rem', background: '#0B63C5', color: '#FFFFFF', border: 0, borderRadius: 10,
            fontSize: 15, fontWeight: 600, cursor: saving || !ready ? 'not-allowed' : 'pointer', opacity: saving || !ready ? 0.6 : 1
          }}>
            {saving ? 'Saving...' : 'Save password and continue'}
          </button>
          {onSignOut && (
            <button type="button" onClick={onSignOut} style={{ marginTop: 12, background: 'transparent', border: 0, color: '#0B63C5', fontWeight: 600, cursor: 'pointer' }}>
              Sign out
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
